import FileResizer from "react-image-file-resizer";

export const resizeProfilePicture = (file) =>
  new Promise((resolve) => {
    FileResizer.imageFileResizer(
      file,
      300,
      300,
      "JPEG",
      100,
      0,
      (file) => {
        resolve(file);
      },
      "file"
    );
  });

// Contentful Images API: resize/recompress assets on their CDN instead of
// downloading the full-size original. No-op for non-Contentful URLs.
export const contentfulImage = (
  url,
  { width, height, quality = 70, fit = "fill", format = "webp" } = {}
) => {
  if (!url) return url;
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.endsWith("ctfassets.net")) return url;
    if (width) parsed.searchParams.set("w", String(width));
    if (height) parsed.searchParams.set("h", String(height));
    if (width && height) parsed.searchParams.set("fit", fit);
    if (format) parsed.searchParams.set("fm", format);
    parsed.searchParams.set("q", String(quality));
    return parsed.toString();
  } catch (e) {
    return url;
  }
};

// `aspectRatio` is opt-in: pass it to crop to a fixed shape, omit it to resize
// on width alone and keep the asset's natural aspect.
export const contentfulSrcSet = (url, widths, options = {}) => {
  if (!url) return undefined;
  const { aspectRatio, ...rest } = options;
  const srcSet = widths
    .map((width) => {
      const resized = contentfulImage(url, {
        width,
        ...(aspectRatio ? { height: Math.round(width / aspectRatio) } : {}),
        ...rest,
      });
      return `${resized} ${width}w`;
    })
    .join(", ");
  // nothing to gain when the URL is not a Contentful asset
  return srcSet.includes("ctfassets.net") ? srcSet : undefined;
};

// AVIF compresses harder than WebP at the same perceived quality, so it gets a
// lower number rather than the same one.
const FORMAT_QUALITY = { avif: 60, webp: 75 };

// Ordered <source> descriptors for a <picture>. The browser takes the first
// type it supports, so AVIF has to come before WebP. Contentful falls back to
// the original encoding when it cannot produce the requested format, and the
// <img> inside the <picture> covers browsers that support neither.
export const contentfulPictureSources = (url, widths, options = {}) => {
  const { quality, ...rest } = options;
  return ["avif", "webp"]
    .map((format) => ({
      type: `image/${format}`,
      srcSet: contentfulSrcSet(url, widths, {
        ...rest,
        format,
        quality: quality ?? FORMAT_QUALITY[format],
      }),
    }))
    .filter((source) => source.srcSet);
};

export const validateImage = (file, maxSizeMB, allowedFormats) => {
  if (!file) return { isValid: false, errorMessage: "No file selected." };

  const fileSizeInMB = file.size / (1024 * 1024);

  if (fileSizeInMB > maxSizeMB) {
    return {
      isValid: false,
      errorMessage: `File size should be less than ${maxSizeMB} MB.`,
    };
  }

  if (!allowedFormats.includes(file.type)) {
    const formatMap = {
      "image/jpeg": "JPEG",
      "image/png": "PNG",
      "image/gif": "GIF",
    };
    const allowedFormatsReadable = allowedFormats.map(
      (format) => formatMap[format]
    );
    let formattedFormats;

    if (allowedFormatsReadable.length === 2) {
      formattedFormats = allowedFormatsReadable.join(" and ");
    } else if (allowedFormatsReadable.length > 2) {
      formattedFormats = `${allowedFormatsReadable
        .slice(0, -1)
        .join(", ")}, or ${
        allowedFormatsReadable[allowedFormatsReadable.length - 1]
      }`;
    } else {
      formattedFormats = allowedFormatsReadable[0];
    }

    return {
      isValid: false,
      errorMessage: `File type should be ${formattedFormats}.`,
    };
  }

  return { isValid: true, errorMessage: "" };
};
